import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { newsletterCurl, normalizeOrigin, websiteCurl, websiteEnv, websiteEnvName, type CreatedWebsiteKey, type WebsiteKey, type WebsiteKeysInfo, type WebsiteKeyValue } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useSession } from '../session';
import './website-forms.css';

const day = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const lines = (text: string) => text.split(/[\n,;]+/).map((t) => t.trim()).filter(Boolean);
const MASK = '••••••••••••••••••••••••••••••••';

/** Zum Kopieren: Schaltfläche mit Rückmeldung */
function CopyButton({ text, label, disabled }: { text: string; label: string; disabled?: boolean }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="btn btn--secondary btn--sm"
      type="button"
      disabled={disabled}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 2000);
        } catch {
          setDone(false);
        }
      }}
    >
      {done ? 'Kopiert' : label}
    </button>
  );
}

/**
 * Ein Schlüssel mit Ein- und Ausblenden. Der Wert wird erst beim Einblenden vom Server geholt (das steht im Änderungsprotokoll) und verschwindet beim Ausblenden wieder aus der Seite.
 * Schlüssel, die sich nicht anzeigen lassen (vom Betreiber angelegt oder älter als diese Funktion), nennen nur ihren Anfang.
 */
function KeyValue({ base, k, initial, onValue }: { base: string; k: WebsiteKey; initial?: string; onValue: (value: string | null) => void }) {
  const [value, setValue] = useState<string | null>(initial ?? null);
  const [error, setError] = useState<string | null>(null);
  const reveal = useMutation({
    mutationFn: async () => (await api<{ data: WebsiteKeyValue }>('GET', `${base}/${k.id}/reveal`)).data.key,
    onSuccess: (key) => {
      setValue(key);
      onValue(key);
      setError(null);
    },
    onError: (e) => setError(errorText(e, 'Der Schlüssel konnte nicht angezeigt werden.')),
  });
  if (k.revokedAt) return <code>ktk_{k.prefix}…</code>;
  if (!k.revealable && !value) return <span><code>ktk_{k.prefix}…</code><span className="muted small"> (lässt sich nicht anzeigen)</span></span>;
  return (
    <div className="website-key">
      <input className="input website-key__value" readOnly aria-label={`Schlüssel ${k.name}`} value={value ?? MASK} type="text" onFocus={(e) => value && e.currentTarget.select()} />
      <div className="row-actions">
        {value ? (
          <button className="btn btn--secondary btn--sm" type="button" onClick={() => { setValue(null); onValue(null); }}>Verbergen</button>
        ) : (
          <button className="btn btn--secondary btn--sm" type="button" disabled={reveal.isPending} onClick={() => reveal.mutate()}>{reveal.isPending ? 'Wird geholt …' : 'Anzeigen'}</button>
        )}
        <CopyButton text={value ?? ''} label="Kopieren" disabled={!value} />
      </div>
      {error && <small className="field__error" role="alert">{error}</small>}
    </div>
  );
}

/**
 * Einrichtung → APIs und Webhooks → Anfragen (Webseite) (KON-237): Wer ein eigenes Anfrageformular auf seiner Webseite hat, legt hier selbst den Schlüssel dafür an, statt ihn bei Wirkia anzufragen. Die Seite zeigt den
 * Schlüssel (ein- und ausblendbar), die Zeilen für die `.env` der Webseite, die Anfragetypen mit ihren Feldern und einen Beispielaufruf. Für alle Pakete, weil jedes Paket Anfragen hat.
 */
export function WebsiteFormsPage({ kind = 'inquiries' }: { kind?: 'inquiries' | 'newsletter' }) {
  const newsletter = kind === 'newsletter';
  const base = newsletter ? '/newsletter-keys' : '/website-keys';
  const client = useQueryClient();
  const { can } = useSession();
  const canWrite = can('setup.integrations');
  const info = useQuery({ queryKey: [base], queryFn: async () => (await api<{ data: WebsiteKeysInfo }>('GET', base)).data });
  const [name, setName] = useState('Webseite');
  const [origins, setOrigins] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editOrigins, setEditOrigins] = useState('');
  const [shown, setShown] = useState<Record<string, string | null>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [typeKey, setTypeKey] = useState('');

  const refresh = () => client.invalidateQueries({ queryKey: [base] });
  const create = useMutation({
    mutationFn: async () => (await api<{ data: CreatedWebsiteKey }>('POST', base, { name, allowedOrigins: lines(origins) })).data,
    onSuccess: (k) => {
      setShown((s) => ({ ...s, [k.id]: k.key }));
      setSelected(k.id);
      setMessage({ ok: true, text: k.revealable ? 'Der Schlüssel ist angelegt. Sie können ihn jederzeit wieder einblenden.' : 'Der Schlüssel ist angelegt. Kopieren Sie ihn jetzt, danach lässt er sich nicht mehr anzeigen.' });
      refresh();
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Der Schlüssel konnte nicht angelegt werden.') }),
  });
  const update = useMutation({
    mutationFn: async (v: { id: string; allowedOrigins: string[] }) => (await api<{ data: WebsiteKey }>('PATCH', `${base}/${v.id}`, { allowedOrigins: v.allowedOrigins })).data,
    onSuccess: () => {
      setEditing(null);
      setMessage({ ok: true, text: 'Die erlaubten Webseiten sind gespeichert.' });
      refresh();
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Speichern hat nicht geklappt.') }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `${base}/${id}`),
    onSuccess: (_r, id) => {
      setShown((s) => ({ ...s, [id]: null }));
      setMessage({ ok: true, text: newsletter ? 'Der Schlüssel ist gelöscht. Anmeldeformulare, die ihn verwenden, können niemanden mehr anmelden.' : 'Der Schlüssel ist gelöscht. Formulare, die ihn verwenden, können keine Anfragen mehr senden.' });
      refresh();
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Löschen hat nicht geklappt.') }),
  });

  if (info.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!info.data) return <div className="alert alert--error" role="alert">Die Angaben konnten nicht geladen werden.</div>;
  const d = info.data;
  const active = d.keys.filter((k) => !k.revokedAt);
  const current = d.keys.find((k) => k.id === selected && !k.revokedAt) ?? active.find((k) => shown[k.id]) ?? null;
  const currentValue = current ? shown[current.id] ?? null : null;
  const placeholder = 'IHR-SCHLUESSEL';
  const type = d.types.find((t) => t.key === typeKey) ?? d.types[0];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);
    create.mutate();
  };

  return (
    <>
      <section className="card">
        <h2>{newsletter ? 'Newsletter-Anmeldung auf Ihrer Webseite' : 'Webseite und Formulare'}</h2>
        {newsletter ? (
          <p className="muted small">
            Hat Ihre Webseite ein Anmeldeformular für den Newsletter, kann es die Anmeldung direkt an Wirkia senden. Die Person erscheint unter Marketing → Abonnenten und bekommt eine Bestätigungsmail, erst danach ist sie angemeldet. Dafür braucht die
            Webseite einen Schlüssel: Legen Sie ihn hier an und tragen Sie ihn in die Einstellungen Ihrer Webseite ein (meist die Datei <code>.env</code>).
          </p>
        ) : (
        <p className="muted small">
          Haben Sie auf Ihrer eigenen Webseite ein Anfrageformular, kann es seine Anfragen direkt an Wirkia senden. Sie landen unter CRM-Abteilung → Anfragen, als wären sie telefonisch eingegangen. Dafür braucht die
          Webseite einen Schlüssel: Legen Sie ihn hier an und tragen Sie ihn in die Einstellungen Ihrer Webseite ein (meist die Datei <code>.env</code>). Wer die Webseite nicht selbst pflegt, gibt die Angaben unten an den Webentwickler weiter.
        </p>
        )}
        {d.trial && <div className="alert alert--warn" role="status">Solange Sie Wirkia testen, gibt es keine Schlüssel für Webseiten. Mit einem Paket entfällt diese Grenze.</div>}
        {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      </section>

      <section className="card">
        <h2>1. Schlüssel</h2>
        <p className="muted small">
          {newsletter ? 'Der Schlüssel kann nur Newsletter-Anmeldungen entgegennehmen und sonst nichts.' : 'Der Schlüssel kann nur Anfragen entgegennehmen und sonst nichts.'} Zusätzlich gilt er nur auf den Webseiten, die Sie eintragen. Sie können bis zu {d.maxKeys} Schlüssel haben, zum Beispiel einen je Webseite.
          Der Schlüssel ist verborgen, bis Sie ihn einblenden. Jedes Einblenden steht im Protokoll.
        </p>

        {d.keys.length > 0 && (
          <div className="table-scroll">
            <table className="table table--cards">
              <thead>
                <tr><th>Name</th><th>Schlüssel</th><th>Erlaubte Webseiten</th><th>Zuletzt benutzt</th><th><span className="sr-only">Aktionen</span></th></tr>
              </thead>
              <tbody>
                {d.keys.map((k) => (
                  <tr key={k.id} className={k.revokedAt ? 'is-muted' : undefined}>
                    <td>{k.name}{k.revokedAt && <span className="badge badge--rejected"> Gesperrt</span>}<div className="muted small">angelegt am {day.format(new Date(k.createdAt))}</div></td>
                    <td data-label="Schlüssel"><KeyValue base={base} k={k} initial={shown[k.id] ?? undefined} onValue={(v) => setShown((s) => ({ ...s, [k.id]: v }))} /></td>
                    <td data-label="Erlaubte Webseiten">
                      {editing === k.id ? (
                        <>
                          <textarea className="input" rows={3} value={editOrigins} onChange={(e) => setEditOrigins(e.target.value)} placeholder="https://www.beispiel.de" aria-label="Erlaubte Webseiten, eine je Zeile" />
                          <div className="form-actions">
                            <button className="btn btn--primary btn--sm" type="button" disabled={update.isPending} onClick={() => update.mutate({ id: k.id, allowedOrigins: lines(editOrigins).map((o) => normalizeOrigin(o) ?? o) })}>Speichern</button>
                            <button className="btn btn--secondary btn--sm" type="button" onClick={() => setEditing(null)}>Abbrechen</button>
                          </div>
                        </>
                      ) : k.allowedOrigins.length > 0 ? (
                        k.allowedOrigins.map((o) => <div key={o}><code>{o}</code></div>)
                      ) : (
                        <span className="muted">Jede Webseite</span>
                      )}
                    </td>
                    <td data-label="Zuletzt benutzt">{k.lastUsedAt ? day.format(new Date(k.lastUsedAt)) : <span className="muted">noch nie</span>}</td>
                    <td className="cell-actions">
                      {canWrite && editing !== k.id && (
                        <div className="row-actions">
                          {!k.revokedAt && <button className="btn btn--secondary btn--sm" type="button" onClick={() => setSelected(k.id)}>Für Beispiel wählen</button>}
                          {!k.revokedAt && <button className="btn btn--secondary btn--sm" type="button" onClick={() => { setEditing(k.id); setEditOrigins(k.allowedOrigins.join('\n')); }}>Webseiten ändern</button>}
                          <button className="btn btn--ghost btn--sm" type="button" disabled={remove.isPending} onClick={() => { if (window.confirm(`Schlüssel „${k.name}“ löschen? ${newsletter ? 'Anmeldeformulare, die ihn verwenden, können danach niemanden mehr anmelden' : 'Formulare, die ihn verwenden, können danach keine Anfragen mehr senden'}. Das lässt sich nicht rückgängig machen.`)) remove.mutate(k.id); }}>Löschen</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {canWrite && !d.trial && active.length < d.maxKeys && (
          <form className="website-add" onSubmit={submit} noValidate>
            <h3>Neuen Schlüssel anlegen</h3>
            <div className="form-grid">
              <label className="field">
                <span>Name</span>
                <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="field">
                <span>Erlaubte Webseiten (eine je Zeile)</span>
                <textarea className="input" rows={3} value={origins} onChange={(e) => setOrigins(e.target.value)} placeholder={'https://www.beispiel.de\nhttps://beispiel.de'} />
                <span className="muted small">So, wie die Adresse im Browser steht, mit https:// und mit oder ohne www. Sendet nur der Server Ihrer Webseite {newsletter ? 'die Anmeldungen' : 'die Anfragen'}, genügt der Eintrag leer. Leer heißt: von jeder Webseite aus.</span>
              </label>
            </div>
            <div className="form-actions">
              <button className="btn btn--primary" type="submit" disabled={create.isPending || name.trim().length < 2}>{create.isPending ? 'Wird angelegt …' : 'Schlüssel anlegen'}</button>
            </div>
          </form>
        )}
      </section>

      <section className="card">
        <h2>2. Einstellungen für Ihre Webseite</h2>
        {!current ? (
          <p className="muted">Blenden Sie oben einen Schlüssel ein oder legen Sie einen an, dann erscheinen hier die fertigen Zeilen.</p>
        ) : (
          <>
            <p className="muted small">Für den Schlüssel „{current.name}“. Tragen Sie diese Zeilen in die <code>.env</code> Ihrer Webseite ein (<code>{websiteEnvName(kind)}</code> ist der Schlüssel {newsletter ? 'für die Newsletter-Anmeldung, der für Anfragen hat einen eigenen Namen' : 'für Anfragen, der für die Newsletter-Anmeldung hat einen eigenen Namen'}). Die Datei gehört nicht ins öffentliche Verzeichnis und nicht in die Versionsverwaltung.</p>
            <pre className="code-block"><code>{websiteEnv(d.apiUrl, currentValue ?? placeholder, kind)}</code></pre>
            <div className="form-actions">
              <CopyButton text={websiteEnv(d.apiUrl, currentValue ?? placeholder, kind)} label="Zeilen kopieren" disabled={!currentValue} />
              {!currentValue && <span className="muted small">Blenden Sie den Schlüssel oben ein, dann lässt sich der Text kopieren.</span>}
            </div>
          </>
        )}
      </section>

      {!newsletter && (
      <section className="card">
        <h2>3. Anfragetypen und Felder</h2>
        <p className="muted small">
          Das Formular sendet den Anfragetyp und seine Felder. Der Schlüssel eines Felds steht in der linken Spalte, genau so schreibt ihn das Formular. Weitere Typen und Felder legen Sie unter Einrichtung → Anfragen → Anfragetypen an.
        </p>
        {d.types.length === 0 ? (
          <p className="muted">Es gibt noch keinen Anfragetyp.</p>
        ) : (
          <div className="website-types">
            {d.types.map((t) => (
              <details key={t.key} open={t.key === (type?.key ?? '')}>
                <summary><strong>{t.label}</strong> <code>{t.key}</code></summary>
                <table className="table table--cards">
                  <thead><tr><th>Feld</th><th>Beschriftung</th><th>Art</th><th>Pflicht</th></tr></thead>
                  <tbody>
                    {t.fields.map((f) => (
                      <tr key={f.key}>
                        <td data-label="Feld"><code>{f.key}</code></td>
                        <td data-label="Beschriftung">{f.label}</td>
                        <td data-label="Art">{f.type}</td>
                        <td data-label="Pflicht">{f.required ? 'ja' : 'nein'}</td>
                      </tr>
                    ))}
                    {t.fields.length === 0 && <tr><td colSpan={4} className="muted">Dieser Typ hat nur die allgemeinen Angaben (Name, E-Mail, Nachricht).</td></tr>}
                  </tbody>
                </table>
              </details>
            ))}
          </div>
        )}
      </section>

      )}

      {newsletter ? (
      <section className="card">
        <h2>3. Beispielaufruf zum Testen</h2>
        <p className="muted small">
          Das Anmeldeformular Ihrer Webseite sendet Name, E-Mail-Adresse und die Einwilligung (<code>consent: true</code>) an Wirkia (<code>POST {d.apiUrl}/marketing/subscribe</code>), am besten von ihrem Server aus, damit der Schlüssel verborgen bleibt. Das Häkchen muss die Person selbst setzen; seinen Wortlaut geben Sie in <code>consentText</code> mit, er dient als Nachweis. Zum Ausprobieren eignet sich dieser Aufruf in einem Terminal:
        </p>
        <pre className="code-block"><code>{newsletterCurl(d.apiUrl, currentValue ?? placeholder)}</code></pre>
        <div className="form-actions">
          <CopyButton text={newsletterCurl(d.apiUrl, currentValue ?? placeholder)} label="Aufruf kopieren" disabled={!currentValue} />
        </div>
      </section>
      ) : (
      <section className="card">
        <h2>4. Beispielaufruf zum Testen</h2>
        <p className="muted small">
          Das Formular Ihrer Webseite sendet die Anfrage an Wirkia (<code>POST {d.apiUrl}/inquiries</code>), am besten von ihrem Server aus, damit der Schlüssel verborgen bleibt. Name, E-Mail und der Zeitpunkt der Einwilligung
          (<code>consentAt</code>) gehören immer dazu. Zum Ausprobieren eignet sich dieser Aufruf in einem Terminal:
        </p>
        {d.types.length > 1 && (
          <label className="field website-type-pick">
            <span>Anfragetyp</span>
            <select className="input" value={type?.key ?? ''} onChange={(e) => setTypeKey(e.target.value)}>
              {d.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
        )}
        <pre className="code-block"><code>{websiteCurl(d.apiUrl, currentValue ?? placeholder, type)}</code></pre>
        <div className="form-actions">
          <CopyButton text={websiteCurl(d.apiUrl, currentValue ?? placeholder, type)} label="Aufruf kopieren" disabled={!currentValue} />
        </div>
        <p className="muted small">
          Die Anfrage erscheint danach unter CRM-Abteilung → Anfragen. Meldet die Schnittstelle einen Fehler, nennt sie das Feld und den Grund. Häufig fehlt <code>consentAt</code> oder ein Pflichtfeld des Anfragetyps. Erscheint beim Senden aus dem Browser ein Fehler zu „CORS“,
          fehlt die Adresse der Webseite beim Schlüssel.
        </p>
      </section>
      )}
    </>
  );
}
